import { Component, EventEmitter, OnDestroy, OnInit, Optional, QueryList, SkipSelf, ViewChildren } from '@angular/core';
import { catchError, finalize, map, mergeMap, take, takeUntil } from 'rxjs/operators';
import { Observable } from 'rxjs/Observable';
import { of as ObservableOf } from 'rxjs/observable/of';
import { forkJoin as ObservableForkJoin } from 'rxjs/observable/forkJoin';
import {
  AbstractBlModal,
  ModalSize,
  ModalButtonType,
  ModalButtonColor,
  ModalButton,
  ModalReason,
  BlModalService,
  BlDialogService,
} from '@blcloud/bl-ng-ui-component';
import {
  EmployeeGuideComponent,
  IMaintenanceOrganization,
  WithAllCompanyDiv,
  IOrganizationMultiSelectChangeParam,
  LoginResourceService,
} from '@blcloud/bl-ng-share-module';
import { ContractConst } from '@blcloud/bl-datamodel/const/contract';
import { AsyncSubject } from 'rxjs/AsyncSubject';
import { IEmployee, IContactDivSetting, IOrganizationInformation, IContactInitDisplaySettingDtl } from '@blcloud/bl-datamodel';
import { LabelUseCategory } from '@blcloud/bl-datamodel/enum/common/label-use-category';
import { ContactExtractTargetMPExistDiv } from '@blcloud/bl-datamodel/enum/deals/contact-extract-target-m-p-exist-div';
import { ContactInitSettingModalService } from './contact-init-setting-modal.service';
import {
  isEmpty as _isEmpty, cloneDeep as _cloneDeep, filter as _filter, isNil as _isNil, forEach as _forEach, find as _find, reject as _reject,
  includes as _includes, map as _map, head as _head, compact as _compact,
} from 'lodash';
import { ApplyPeriodCriteriaDivArray, ApplyPeriodCriteriaDiv } from '@blcloud/bl-datamodel/enum/bizcmn/apply-period-criteria-div';
import { MonthWeekDayDivArray, MonthWeekDayDiv } from '@blcloud/bl-datamodel/enum/bizcmn/month-week-day-div';
import * as moment from 'moment';
import { IContactInitDisplaySettingEx } from './contact-init-setting-modal.interface';
import { ContactDateSearchDiv } from '@blcloud/bl-datamodel/enum/deals/contact-date-search-div';
import { HttpStatus } from '@blcloud/bl-ng-common';
import {
  CONTACT_DATE_SEARCH_DIV_LIST,
  CONTACT_EXTRACT_TARGET_MP_EXIST_DIV_LIST,
  CONTACT_EXTRACT_TARGET_ENTRUSTED_CONTRACT_EXIST_DIV_LIST,
} from '../../shared/contact/contact-page.interface';
import {
  ContactExtractTargetEntrustedContractExistDiv
} from '@blcloud/bl-datamodel/enum/lease/contact-extract-target-entrusted-contract-exist-div';
import { ContactExtractDmSendDiv } from '@blcloud/bl-datamodel/enum/approach/contact-extract-dm-send-div';
import {
  OrgMasterKind,
  SORT_ORDER_DIV_LIST,
  SortOrderDiv,
  isOrgMasterSelectionKeyInOrganizations,
} from '../../page/contact-list-page/contact-list-page.interface';
import { ContactListPageService } from '../../page/contact-list-page/contact-list-page.service';
import { OrgMasterSelectSearchComponent } from '../../page/contact-list-page/org-master-select-search/org-master-select-search.component';
import { ContactService } from '../../shared/contact/contact.service';

/**
 * コンタクト初期表示設定コンポーネント
 */
@Component({
  templateUrl: './contact-init-setting-modal.component.html',
  styleUrls: ['./contact-init-setting-modal.component.scss'],
  providers: [
    ContactInitSettingModalService,
    {
      provide: ContactListPageService,
      useFactory: (
        modal: ContactInitSettingModalComponent,
        parentService: ContactListPageService,
      ) => modal.resolveContactListPageServiceForChildComponents(parentService),
      deps: [ContactInitSettingModalComponent, [new Optional(), new SkipSelf(), ContactListPageService]],
    },
  ]
})
export class ContactInitSettingModalComponent extends AbstractBlModal implements OnInit, OnDestroy {

  private readonly onDestroy$ = new EventEmitter();

  /** 画面タイトル */
  readonly headerTitle = 'コンタクト初期表示設定';

  /** コンタクト初期表示設定 */
  contactInitDisplaySettingEx: IContactInitDisplaySettingEx = {};
  /** コンタクト初期表示設定詳細(一覧) */
  contactInitDisplaySettingDtlGrid: IContactInitDisplaySettingDtl = {};
  /** コンタクト初期表示設定詳細(カレンダー) */
  contactInitDisplaySettingDtlCalendar: IContactInitDisplaySettingDtl = {};

  /** 実施内容ごとに選択するチェックボックス選択(カレンダー) */
  isCalendarSelectIndividually = false;
  /** コンタクトグループ区分チェックボックス選択(カレンダー) */
  isCalendarSelectContactCheckDivArray = false;
  /** 実施内容ごとに選択するチェックボックス選択(一覧) */
  isGridSelectIndividually = false;
  /** コンタクトグループ区分チェックボックス選択(一覧) */
  isGridSelectContactCheckDivArray = false;

  /**
   * ログイン組織通知イベント
   */
  loginOrganizationSubject: AsyncSubject<IMaintenanceOrganization> = new AsyncSubject();

  /** 組織情報表示区分 */
  WithAllCompanyDiv = WithAllCompanyDiv;

  /** ログイン担当者従業員コード */
  loginUserEmpCode: string;
  /** ログイン担当者従業員名称 */
  loginUserEmpName: string;
  /** ログイン担当者の組織コード */
  loginUserOrganizationCode: string;

  /** 従業員使用非活性フラグ（カレンダー） */
  employeeDisableFlgCalendar = false;
  /** 担当者名（カレンダー） */
  calendarPicName: string;

  /** 従業員使用非活性フラグ（コンタクト一覧） */
  employeeDisableFlgContactList = false;
  /** 担当者名（カレンダー） */
  contactListPicName: string;

  /** コンタクトグループ区分 */
  contactCheckDivArray: { code: number, name: string }[] = [
    { code: 1, name: '車検' }, { code: 2, name: '点検' }, { code: 3, name: 'その他' }
  ];

  /** コンタクト実施内容配列 */
  contactImplDivArray: { code: number, name: string, div: number }[] = [];

  /** 自動生成履歴用の実施内容 */
  autoGenerationHistory: number[] = [90018, 90019, 90020, 90021, 90022, 90023, 90024];

  /** 適用期間基準区分 */
  applyPeriodCriteriaDivItems = ApplyPeriodCriteriaDivArray.map(div => {
    return { code: div.value, name: div.text };
  });

  /** 月週日区分 */
  monthWeekDayDivItems = MonthWeekDayDivArray.map(div => {
    return { code: div.value, name: div.text };
  });

  /** ラベル利用カテゴリ */
  readonly LabelUseCategory = LabelUseCategory;

  /** 組織別マスタの種類 */
  readonly OrgMasterKind = OrgMasterKind;

  /** コンタクト日時検索区分 */
  readonly CONTACT_DATE_SEARCH_DIV_LIST = CONTACT_DATE_SEARCH_DIV_LIST;

  /** コンタクト対象MP存在区分 */
  readonly CONTACT_EXTRACT_TARGET_MP_EXIST_DIV_LIST = CONTACT_EXTRACT_TARGET_MP_EXIST_DIV_LIST;

  /** コンタクト抽出対象リース受託契約有無区分 */
  readonly CONTACT_EXTRACT_TARGET_ENTRUSTED_CONTRACT_EXIST_DIV_LIST =
    CONTACT_EXTRACT_TARGET_ENTRUSTED_CONTRACT_EXIST_DIV_LIST;

  /** ソート順 */
  readonly SORT_ORDER_DIV_LIST = SORT_ORDER_DIV_LIST;

  /** コンタクト抽出対象顧客DM案内可否*/
  _customerDmSendDivList: Array<{ code: string, name: string }> = [];

  /** コンタクト抽出対象車両DM案内可否 */
  _vehicleDmSendDivList: Array<{ code: string, name: string }> = [];

  /** 実施手段 */
  _contactDivSettingList: IContactDivSetting[] = [];

  /** 失注理由2列UI選択キー */
  contactLostReasonOrgMasterKeys: string[] = [];

  /** コンタクト区分2列UI選択キー */
  contactItemOrgMasterKeys: string[] = [];

  /** 2列UIマスタ再取得トークン */
  orgMasterRefreshToken = 0;

  /** 組織マルチセレクト、マスタ取得済みであるか */
  private organizationMultiSelectReadyReceived = false;

  /** readyEvent で受け取った組織候補（名称表示用） */
  private organizationCandidates: IOrganizationInformation[] = [];

  /** 2列UI子コンポーネント */
  @ViewChildren(OrgMasterSelectSearchComponent)
  private orgMasterSelectSearchComponents: QueryList<OrgMasterSelectSearchComponent>;

  /** MPオプション表示 */
  isAvailableMaintePack = false;

  /** リースオプション表示 */
  isAvailableEntrustedLease = false;

  /** 一覧向け組織コード（複数） */
  get gridOrganizationCodes(): string[] {
    return this.contactInitDisplaySettingDtlGrid.organizationCodeList || [];
  }

  set gridOrganizationCodes(codes: string[]) {
    this.contactInitDisplaySettingDtlGrid.organizationCodeList = codes || [];
  }

  /** 適用開始期間基準が「本日」かどうかを返します。 */
  get isApplyStartPeriodToday() {
    return this.contactInitDisplaySettingDtlGrid.applyStartPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today;
  }

  /** 適用終了期間基準が「本日」かどうかを返します。 */
  get isApplyEndPeriodToday() {
    return this.contactInitDisplaySettingDtlGrid.applyEndPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today;
  }

  /**
   * モーダルボタンタイプ
   * @ignore
   */
  ModalButtonType = ModalButtonType;

  /**
   * モーダルボタンカラー
   * @ignore
   */
  ModalButtonColor = ModalButtonColor;

  /** グリッドからの次(下)のフォーカス移動先指定 */
  nextFocusTarget = {
    focus: () => { this.content.focusFooterButton(ModalButton.Cancel); }
  };

  // --------------------------------------------------
  // Life Cycle

  /**
   * コンストラクタ
   * @param contactInitSettingModalService コンタクト初期表示設定モーダルサービス
   * @param loginResourceService ログインリソースサービス
   * @param modalService モーダルサービス
   * @param dialogService ダイアログサービス
   * @param contactListPageService コンタクト一覧ページサービス
   */
  constructor(
    private contactInitSettingModalService: ContactInitSettingModalService,
    private loginResourceService: LoginResourceService,
    private contactService: ContactService,
    private modalService: BlModalService,
    private dialogService: BlDialogService,
    @SkipSelf() @Optional() private contactListPageService: ContactListPageService,
  ) {
    super();
  }

  /** 2列UI子コンポーネントが注入する一覧ページサービス（一覧起動時は一覧と同一インスタンス） */
  resolveContactListPageServiceForChildComponents(moduleService: ContactListPageService): ContactListPageService {
    if (!_isNil(this.data) && !_isNil(this.data.contactListPageService)) {
      return this.data.contactListPageService;
    }
    return moduleService;
  }

  /** 2列UIに渡す一覧サービス（テンプレート用） */
  get orgMasterContactListPageService(): ContactListPageService {
    return this.sharedContactListPageService;
  }

  /**
   * 一覧ページと同一の ContactListPageService を使う。カレンダー起動時はモジュール提供のインスタンス。
   */
  private get sharedContactListPageService(): ContactListPageService {
    // 一覧起動時は親ページのサービスインスタンスを共有
    if (!_isNil(this.data) && !_isNil(this.data.contactListPageService)) {
      return this.data.contactListPageService;
    }
    // カレンダー起動時はモジュール提供のインスタンスを使用
    return this.contactListPageService;
  }

  ngOnDestroy(): void {
    this.onDestroy$.emit();
  }

  ngOnInit() {
    const extractDmSendDivCodes = [
      ContactExtractDmSendDiv.Send,
      ContactExtractDmSendDiv.NoSend,
      ContactExtractDmSendDiv.All,
    ];
    this._customerDmSendDivList = extractDmSendDivCodes.map(code => ({
      code,
      name: this.getDmSendDivDisplayName(code, 'お客様'),
    }));
    this._vehicleDmSendDivList = extractDmSendDivCodes.map(code => ({
      code,
      name: this.getDmSendDivDisplayName(code, '車両'),
    }));

    this.showLoading();
    // 初期表示情報取得
    this.contactInitSettingModalService.getLoginUserEmployeeBindModel().pipe(
      mergeMap(loginUserEmployee => {
        this.loginUserEmpCode = loginUserEmployee.employeeCode;
        this.loginUserEmpName = loginUserEmployee.employeeName;
        this.loginUserOrganizationCode = loginUserEmployee.organizationCode;
        return ObservableForkJoin(
          this.contactInitSettingModalService.getContactInitDisplaySetting(loginUserEmployee.employeeCode),
          this.contactInitSettingModalService.getContactExecuteItem(loginUserEmployee.organizationCode),
          this.loginResourceService.isAvailableService(ContractConst.SERVICEID_OPT_MAC_MAINTE_PACK_BASE),
          this.loginResourceService.isAvailableService(ContractConst.SERVICEID_OPT_MAC_LEASE_VEHICLE_MGT),
        );
      }),
      finalize(() => this.hideLoading()),
      takeUntil(this.onDestroy$),
    ).subscribe(
      responses => {
        this.isAvailableMaintePack = responses[2];
        this.isAvailableEntrustedLease = responses[3];
        if (responses[0] === null) {
          // 初期表示情報が登録されていなかった場合、isRegistをtrueに設定。呼び元のspaでputするかpostするかの判断に使用する。
          this.contactInitDisplaySettingEx.isRegist = true;

          this.employeeDisableFlgContactList = false;
          this.employeeDisableFlgCalendar = false;
          this.contactInitDisplaySettingDtlGrid.contactCheckDivList = [];
          this.contactInitDisplaySettingDtlCalendar.contactCheckDivList = [];
          this.contactInitDisplaySettingDtlGrid.contactDateSearchDiv = ContactDateSearchDiv.ContactScheduleDateTime;
          this.contactInitDisplaySettingDtlGrid.contactExtractCustomerDmSendDiv = ContactExtractDmSendDiv.Send;
          this.contactInitDisplaySettingDtlGrid.contactExtractVehicleDmSendDiv = ContactExtractDmSendDiv.Send;
          if (this.isAvailableMaintePack) {
            this.contactInitDisplaySettingDtlGrid.contactExtractMPExistDiv = ContactExtractTargetMPExistDiv.All;
          }
          if (this.isAvailableEntrustedLease) {
            this.contactInitDisplaySettingDtlGrid.contactExtractEntrustedContractExistDiv =
              ContactExtractTargetEntrustedContractExistDiv.All;
          }
          this.contactInitDisplaySettingDtlGrid.customerLabelCodeList = [];
          this.contactInitDisplaySettingDtlGrid.contactDivDetailCodeList = [];
          this.contactInitDisplaySettingDtlGrid.organizationCodeList = [];
          this.contactInitDisplaySettingDtlGrid.contactExtractResultSortOrderDiv = SortOrderDiv.CustomerNameKana;
          this.loadContactDivSettingList(this.loginUserOrganizationCode);
        } else if (!_isEmpty(responses[0])) {
          // 初期表示情報有りの場合
          this.contactInitDisplaySettingEx.isRegist = false;
          this.contactInitDisplaySettingEx = _cloneDeep(responses[0]);
          this.contactInitDisplaySettingDtlGrid = _cloneDeep(
            responses[0].contactInitDisplaySettingDtlGrid,
          ) || {};
          this.contactInitDisplaySettingDtlCalendar = _cloneDeep(responses[0].contactInitDisplaySettingDtlCalendar);
          // 旧レコード（一覧組織コード配列が未設定）のみ、代表組織コード1件から組織コード配列を復元
          if (_isNil(this.contactInitDisplaySettingDtlGrid.organizationCodeList)
            && !_isEmpty(this.contactInitDisplaySettingDtlGrid.organizationCode)) {
            this.contactInitDisplaySettingDtlGrid.organizationCodeList = [this.contactInitDisplaySettingDtlGrid.organizationCode];
          }
          if (_isNil(this.contactInitDisplaySettingDtlGrid.contactDateSearchDiv)) {
            this.contactInitDisplaySettingDtlGrid.contactDateSearchDiv = ContactDateSearchDiv.ContactScheduleDateTime;
          }
          if (_isNil(this.contactInitDisplaySettingDtlGrid.contactExtractCustomerDmSendDiv)) {
            this.contactInitDisplaySettingDtlGrid.contactExtractCustomerDmSendDiv = ContactExtractDmSendDiv.Send;
          }
          if (_isNil(this.contactInitDisplaySettingDtlGrid.contactExtractVehicleDmSendDiv)) {
            this.contactInitDisplaySettingDtlGrid.contactExtractVehicleDmSendDiv = ContactExtractDmSendDiv.Send;
          }
          if (this.isAvailableMaintePack && _isNil(this.contactInitDisplaySettingDtlGrid.contactExtractMPExistDiv)) {
            this.contactInitDisplaySettingDtlGrid.contactExtractMPExistDiv = ContactExtractTargetMPExistDiv.All;
          }
          if (this.isAvailableEntrustedLease) {
            this.contactInitDisplaySettingDtlGrid.contactExtractEntrustedContractExistDiv =
              this.sharedContactListPageService.normalizeContactExtractEntrustedContractExistDivForRadio(
                this.contactInitDisplaySettingDtlGrid.contactExtractEntrustedContractExistDiv,
                ContactExtractTargetEntrustedContractExistDiv.All,
              );
          }
          if (_isNil(this.contactInitDisplaySettingDtlGrid.customerLabelCodeList)) {
            this.contactInitDisplaySettingDtlGrid.customerLabelCodeList = [];
          }
          if (_isNil(this.contactInitDisplaySettingDtlGrid.contactDivDetailCodeList)) {
            this.contactInitDisplaySettingDtlGrid.contactDivDetailCodeList = [];
          } else {
            this.contactInitDisplaySettingDtlGrid.contactDivDetailCodeList =
              _compact(this.contactInitDisplaySettingDtlGrid.contactDivDetailCodeList);
          }
          if (_isNil(this.contactInitDisplaySettingDtlGrid.organizationCodeList)) {
            this.contactInitDisplaySettingDtlGrid.organizationCodeList = [];
          }
          if (_isNil(this.contactInitDisplaySettingDtlGrid.contactExtractResultSortOrderDiv)) {
            this.contactInitDisplaySettingDtlGrid.contactExtractResultSortOrderDiv = SortOrderDiv.CustomerNameKana;
          }
          this.restoreOrgMasterKeysFromDtlGrid();
          const gridPrimaryOrg = _head(this.gridOrganizationCodes) || this.loginUserOrganizationCode;
          this.loadContactDivSettingList(gridPrimaryOrg);
          this.isCalendarSelectContactCheckDivArray = _isEmpty(this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList);
          this.isGridSelectContactCheckDivArray = _isEmpty(this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList);
          this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList = this.checkContactGroupDiv(
            this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList,
            this.contactInitDisplaySettingDtlGrid.contactCheckDivList, responses[1]);
          this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList = this.checkContactGroupDiv(
            this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList,
            this.contactInitDisplaySettingDtlCalendar.contactCheckDivList, responses[1]);
          ObservableForkJoin(
            this.contactInitSettingModalService.getEmployee(this.contactInitDisplaySettingDtlGrid.picCode),
            this.contactInitSettingModalService.getEmployee(this.contactInitDisplaySettingDtlCalendar.picCode),
          ).pipe(takeUntil(this.onDestroy$)).subscribe(
            employeeInfo => {
              if (!_isEmpty(employeeInfo[0])) {
                this.contactInitDisplaySettingDtlGrid.picCode = employeeInfo[0].employeeCode;
                this.contactListPicName = employeeInfo[0].employeeName;
                if (this.loginUserEmpCode === employeeInfo[0].employeeCode) {
                  this.employeeDisableFlgContactList = true;
                } else {
                  this.employeeDisableFlgContactList = false;
                }
              }
              if (!_isEmpty(employeeInfo[1])) {
                this.contactInitDisplaySettingDtlCalendar.picCode = employeeInfo[1].employeeCode;
                this.calendarPicName = employeeInfo[1].employeeName;
                if (this.loginUserEmpCode === employeeInfo[1].employeeCode) {
                  this.employeeDisableFlgCalendar = true;
                } else {
                  this.employeeDisableFlgCalendar = false;
                }
              }
            }
          );
        }
        // 自動生成履歴用の実施内容以外をリスト項目とする
        this.contactImplDivArray = [];
        _forEach(responses[1], contactImpl => {
          if (!_includes(this.autoGenerationHistory, contactImpl.code)) {
            this.contactImplDivArray.push(contactImpl);
          }
        });
        this.initOrgMasterUiAfterSettingsLoaded();
      }
    );
  }

  // --------------------------------------------------
  // AbstractModal Override

  /**
   * @override
   */
  getResults() {
    this.contactInitDisplaySettingEx.contactInitDisplaySettingDtlCalendar = this.contactInitDisplaySettingDtlCalendar;
    this.contactInitDisplaySettingEx.contactInitDisplaySettingDtlGrid = this.contactInitDisplaySettingDtlGrid;
    return this.contactInitDisplaySettingEx;
  }

  /**
   * @override
   */
  defaultOption() {
    return {
      size: ModalSize.Large,
    };
  }

  /**
   * 初期表示設定内容をクリアする
   */
  onClear() {
    // 設定値をクリア（削除）する
    this.dialogService.confirm('コンタクト初期表示設定を削除します。よろしいですか？').subscribe(
      modalRef => {
        if (modalRef.reason === ModalReason.Done) {
          this.showLoading();
          this.contactInitSettingModalService.deleteContactInitDisplaySetting(this.contactInitDisplaySettingEx).subscribe(
            () => {
              this.employeeDisableFlgContactList = false;
              this.employeeDisableFlgCalendar = false;
              this.calendarPicName = '';
              this.contactListPicName = '';
              this.contactInitDisplaySettingDtlGrid = {};
              this.contactInitDisplaySettingDtlCalendar = {};
              this.contactInitDisplaySettingDtlGrid.contactCheckDivList = [];
              this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList = [];
              this.contactInitDisplaySettingDtlCalendar.contactCheckDivList = [];
              this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList = [];
              this.contactLostReasonOrgMasterKeys = [];
              this.contactItemOrgMasterKeys = [];
              this.contactInitDisplaySettingDtlGrid.contactExtractLostReasonCodeList = [];
              this.contactInitDisplaySettingDtlGrid.contacExtractItemCodeList = [];
              this._contactDivSettingList = [];
              this.contactInitDisplaySettingDtlGrid.contactDateSearchDiv = ContactDateSearchDiv.ContactScheduleDateTime;
              this.contactInitDisplaySettingDtlGrid.contactExtractCustomerDmSendDiv = ContactExtractDmSendDiv.Send;
              this.contactInitDisplaySettingDtlGrid.contactExtractVehicleDmSendDiv = ContactExtractDmSendDiv.Send;
              if (this.isAvailableMaintePack) {
                this.contactInitDisplaySettingDtlGrid.contactExtractMPExistDiv = ContactExtractTargetMPExistDiv.All;
              }
              if (this.isAvailableEntrustedLease) {
                this.contactInitDisplaySettingDtlGrid.contactExtractEntrustedContractExistDiv =
                  ContactExtractTargetEntrustedContractExistDiv.All;
              }
              this.contactInitDisplaySettingDtlGrid.customerLabelCodeList = [];
              this.contactInitDisplaySettingDtlGrid.contactDivDetailCodeList = [];
              this.contactInitDisplaySettingDtlGrid.organizationCodeList = [];
              this.contactInitDisplaySettingDtlGrid.contactExtractResultSortOrderDiv = SortOrderDiv.CustomerNameKana;
              this.loadContactDivSettingList(this.loginUserOrganizationCode);
              this.contactInitDisplaySettingEx.isRegist = true;
              this.contactInitDisplaySettingEx.isClear = true;
              this.hideLoading();
              modalRef.hide();
            },
            (error) => {
              this.hideLoading();
              // 削除に失敗した場合は通知メッセージのみ表示する
              if (error.status !== HttpStatus.NotFound) {
                this.dialogService.error('コンタクト初期表示設定の削除に失敗しました。').subscribe(errorModalRef => {
                  errorModalRef.hide();
                });
              }
            },
          );
        } else {
          modalRef.hide();
        }
      }
    );
  }

  /**
   * モーダル終了イベント
   * @param reason　モーダル終了理由
   */
  dismiss(reason: ModalReason) {
    if (reason === ModalReason.Done) {
      if (!this.validate()) {
        return;
      }
      this.showLoading();
      // 2列UI選択をコンタクト抽出対象コード配列等の保存形式へ変換してから確認ダイアログへ進む
      this.prepareInitDisplayGridForSave().pipe(
        finalize(() => this.hideLoading()),
        takeUntil(this.onDestroy$),
      ).subscribe(
        () => {
          this.dialogService.confirm('コンタクト初期表示設定を変更します。よろしいですか？').subscribe(
            modalRef => {
              if (modalRef.reason === ModalReason.Done) {
                this.contactInitDisplaySettingEx.isClear = false;
                this.emitDismissEvent(reason);
              }
              modalRef.hide();
            },
          );
        },
        () => {
          this.dialogService.error('コンタクト初期表示設定の保存準備に失敗しました。').subscribe(errorModalRef => errorModalRef.hide());
        },
      );
    } else {
      this.emitDismissEvent(reason);
    }
  }

  // --------------------------------------------------
  // 内部処理

  /**
   * コンタクトグループ区分チェックボックス押下(カレンダー)
   * @param event
   */
  onChangeCalendarContactCheckDivArray(event: any) {
    const checkItems = _filter(event, item => !_isNil(item));

    if (checkItems.length > 0) {
      this.isCalendarSelectContactCheckDivArray = true;
      const selectedValues = _forEach(event, selectedCheckBox => {
        return selectedCheckBox.value;
      });
      this.contactInitDisplaySettingDtlCalendar.contactCheckDivList = selectedValues;
      // 実施内容区分(チェックボックス)に対応する実施内容(マルチセレクト)を選択状態にする
      const numbers: number[] = [];
      selectedValues.forEach(value => {
        this.contactImplDivArray.forEach(contactImplDiv => {
          if (value === contactImplDiv.div) {
            numbers.push(contactImplDiv.code);
          }
        });
      });
      this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList = numbers;
    } else {
      this.isCalendarSelectContactCheckDivArray = false;
      this.contactInitDisplaySettingDtlCalendar.contactCheckDivList = [];
      this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList = [];
    }
  }

  /**
   * 実施内容ごとに選択するチェックボックス押下(カレンダー)
   * @param event
   */
  onChangeIsCalendarSelectIndividually(event: boolean) {
    if (!event) {
      this.contactInitDisplaySettingDtlCalendar.contactCheckDivList = [];
      this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList = [];
    }
  }

  /**
   * コンタクト実施内容セレクトボックス押下(カレンダー)
   * @param event
   */
  onChangeCalendarContactImplDiv(event: any) {
    if (event.checked) {
      this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList.push(event.item.dropDownValue);
    } else {
      this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList =
        _filter(this.contactInitDisplaySettingDtlCalendar.contactExecuteItemCodeList,
          code => code !== event.item.dropDownValue
        );
      const findItem = _find(this.contactImplDivArray, item => item.code === event.item.dropDownValue);
      if (findItem) {
        // 実施内容のグループ区分を再設定
        this.contactInitDisplaySettingDtlCalendar.contactCheckDivList
          = _reject(this.contactInitDisplaySettingDtlCalendar.contactCheckDivList, div => div === findItem.div);
      }
    }
  }

  /**
   * コンタクトグループ区分チェックボックス押下(一覧)
   * @param event
   */
  onChangeGridContactCheckDivArray(event: any) {
    const checkItems = _filter(event, item => !_isNil(item));

    if (checkItems.length > 0) {
      this.isGridSelectContactCheckDivArray = true;
      const selectedValues = _forEach(event, selectedCheckBox => {
        return selectedCheckBox.value;
      });
      this.contactInitDisplaySettingDtlGrid.contactCheckDivList = selectedValues;
      // 実施内容区分(チェックボックス)に対応する実施内容(マルチセレクト)を選択状態にする
      const numbers: number[] = [];
      selectedValues.forEach(value => {
        this.contactImplDivArray.forEach(contactImplDiv => {
          if (value === contactImplDiv.div) {
            numbers.push(contactImplDiv.code);
          }
        });
      });
      this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList = numbers;
    } else {
      this.isGridSelectContactCheckDivArray = false;
      this.contactInitDisplaySettingDtlGrid.contactCheckDivList = [];
      this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList = [];
    }
  }

  /**
   * 実施内容ごとに選択するチェックボックス押下(一覧)
   * @param event
   */
  onChangeIsGridSelectIndividually(event: boolean) {
    if (!event) {
      this.contactInitDisplaySettingDtlGrid.contactCheckDivList = [];
      this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList = [];
    }
  }

  /**
   * コンタクト実施内容セレクトボックス押下(一覧)
   * @param event
   */
  onChangeGridContactImplDiv(event: any) {
    if (event.checked) {
      this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList.push(event.item.dropDownValue);
    } else {
      this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList =
        _filter(this.contactInitDisplaySettingDtlGrid.contactExecuteItemCodeList,
          code => code !== event.item.dropDownValue
        );
      const findItem = _find(this.contactImplDivArray, item => item.code === event.item.dropDownValue);
      if (findItem) {
        // 実施内容のグループ区分を再設定
        this.contactInitDisplaySettingDtlGrid.contactCheckDivList
          = _reject(this.contactInitDisplaySettingDtlGrid.contactCheckDivList, div => div === findItem.div);
      }
    }
  }

  /**
   * コンタクトグループ区分に紐づくコンタクト実施内容のチェック処理
   * @param targetItemCodeList コンタクト実施内容コード配列
   * @param contactCheckDivList コンタクトグループ区分
   * @param contactExecuteItemList コンタクト実施内容配列
   * @returns コンタクト実施内容コード配列
   */
  private checkContactGroupDiv(
    targetItemCodeList: number[],
    contactCheckDivList: number[],
    contactExecuteItemList: { code: number; name: string; div: number; }[]
  ): number[] {
    if (contactCheckDivList.length === 0) {
      return targetItemCodeList;
    }

    // コンタクトグループ区分に紐づくコンタクト実施内容を抽出する
    const newItemCodeList = _filter(contactExecuteItemList, item =>
      _includes(contactCheckDivList, item.div)
    ).map(item => item.code);

    // 既存チェック分をマージ
    _forEach(targetItemCodeList, code => {
      if (!_includes(newItemCodeList, code)) {
        newItemCodeList.push(code);
      }
    });

    return newItemCodeList;
  }

  /**
   * 担当者ガイドボタン押下
   * @param propertyId　カレンダーまたは一覧
   * @param btnEl　フォーカスターゲット
   */
  onClickEmployeeGuide(propertyId: string, btnEl) {

    this.modalService.show(EmployeeGuideComponent).pipe().subscribe(
      modalRef => {
        if (modalRef.reason === ModalReason.Done) {
          const result = <IEmployee>modalRef.getResults();
          if (!_isEmpty(result)) {
            if (propertyId === 'calendar') {
              this.contactInitDisplaySettingDtlCalendar.picCode = result.employeeCode;
              this.calendarPicName = result.employeeName;
              if (this.loginUserEmpCode === result.employeeCode) {
                this.employeeDisableFlgCalendar = true;
              } else {
                this.employeeDisableFlgCalendar = false;
              }
            } else if (propertyId === 'contactList') {
              this.contactInitDisplaySettingDtlGrid.picCode = result.employeeCode;
              this.contactListPicName = result.employeeName;
              if (this.loginUserEmpCode === result.employeeCode) {
                this.employeeDisableFlgContactList = true;
              } else {
                this.employeeDisableFlgContactList = false;
              }
            }
          }
        }
        modalRef.hide(btnEl);
      }
    );
  }

  /**
   * 従業員ガイド制御処理
   *
   * @param checked　チェック状態
   * @param propertyId　カレンダーまたは一覧
   */
  onChangeEmployeeFlg(checked: boolean, propertyId: string) {
    if (propertyId === 'calendar') {
      if (checked === false) {
        this.employeeDisableFlgCalendar = false;
      } else {
        this.employeeDisableFlgCalendar = true;
        this.contactInitDisplaySettingDtlCalendar.picCode = this.loginUserEmpCode;
        this.calendarPicName = this.loginUserEmpName;
      }
    } else if (propertyId === 'contactList') {
      if (checked === false) {
        this.employeeDisableFlgContactList = false;
      } else {
        this.employeeDisableFlgContactList = true;
        this.contactInitDisplaySettingDtlGrid.picCode = this.loginUserEmpCode;
        this.contactListPicName = this.loginUserEmpName;
      }
    }
  }

  /**
   * 従業員コード手入力
   * @param propertyId　カレンダーまたは一覧
   * @param value　入力値
   */
  changePicCode(propertyId: string, value: string) {
    this.contactInitSettingModalService.getEmployee(value).pipe(takeUntil(this.onDestroy$)).subscribe(
      emp => {
        if (!_isEmpty(emp)) {
          if (propertyId === 'calendar') {
            this.contactInitDisplaySettingDtlCalendar.picCode = emp.employeeCode;
            this.calendarPicName = emp.employeeName;
            if (this.loginUserEmpCode === emp.employeeCode) {
              this.employeeDisableFlgCalendar = true;
            } else {
              this.employeeDisableFlgCalendar = false;
            }
          } else if (propertyId === 'contactList') {
            this.contactInitDisplaySettingDtlGrid.picCode = emp.employeeCode;
            this.contactListPicName = emp.employeeName;
            if (this.loginUserEmpCode === emp.employeeCode) {
              this.employeeDisableFlgContactList = true;
            } else {
              this.employeeDisableFlgContactList = false;
            }
          }
        } else {
          if (propertyId === 'calendar') {
            this.contactInitDisplaySettingDtlCalendar.picCode = '';
            this.calendarPicName = '';
          } else if (propertyId === 'contactList') {
            this.contactInitDisplaySettingDtlGrid.picCode = '';
            this.contactListPicName = '';
          }
        }
      }
    );
  }

  /**
   * 整合性チェック処理
   */
  /**
   * 組織マルチセレクト変更
   * @param param 組織複数選択変更パラメータ
   */
  onChangeOrganizationMultiSelect(param: IOrganizationMultiSelectChangeParam): void {
    // 組織複数選択変更パラメータをマージ
    this.sharedContactListPageService.mergeOrganizationDisplayInfo(param.organizations);
    this.mergeOrganizationCandidateNames();
    this.gridOrganizationCodes = _map(param.organizations, o => o.organizationCode);
    this.removeSelectKeysOfUnselectedOrganizations();
    this.syncOrgMasterUiAfterOrganizationContextReady({ preserveOrgMasterServiceCache: false });
    const primaryOrg = _head(this.gridOrganizationCodes);
    const orgForDivSetting = !_isEmpty(primaryOrg) ? primaryOrg : this.loginUserOrganizationCode;
    this.loadContactDivSettingList(orgForDivSetting);
  }

  /**
   * 組織マルチセレクト候補取得完了
   * @param organizations 候補組織一覧
   */
  onOrganizationMultiSelectReady(organizations: IOrganizationInformation[]): void {
    this.organizationMultiSelectReadyReceived = true;
    this.organizationCandidates = organizations || [];
    // 一覧起動かつ候補組織登録済みなら再登録せず組織名称マージのみ行う
    if (this.isOpenedFromContactListPage()
      && this.sharedContactListPageService.hasRegisteredSelectableOrganizations()) {
      this.sharedContactListPageService.mergeOrganizationDisplayInfo(organizations);
    } else {
      this.sharedContactListPageService.registerSelectableOrganizations(organizations);
    }
    this.removeSelectKeysOfUnselectedOrganizations();
    // 登録直後判定のため再登録後もキャッシュ維持になり得る。マージのみ経路で候補キャッシュクリアを省略する
    const preserveCache = this.isOpenedFromContactListPage()
      && this.sharedContactListPageService.hasRegisteredSelectableOrganizations();
    this.syncOrgMasterUiAfterOrganizationContextReady({ preserveOrgMasterServiceCache: preserveCache });
  }

  /** 顧客ラベル変更 */
  onChangeCustomerLabel(codeList: number[]): void {
    this.contactInitDisplaySettingDtlGrid.customerLabelCodeList = codeList;
  }

  /** 失注理由2列UI変更 */
  onChangeContactLostReasonOrgMasterKeys(keys: string[]): void {
    this.contactLostReasonOrgMasterKeys = keys;
  }

  /** コンタクト区分2列UI変更 */
  onChangeContactItemOrgMasterKeys(keys: string[]): void {
    this.contactItemOrgMasterKeys = keys;
  }

  /** 実施手段変更（bl-multi-select の changeItemsEvent はコード配列） */
  onChangeContactDivSetting(contactDivSettingCodeList: number[]): void {
    this.contactInitDisplaySettingDtlGrid.contactDivDetailCodeList = contactDivSettingCodeList || [];
  }

  /**
   * 組織に紐づく実施手段一覧を読み込みます。
   * @param organizationCode 組織コード
   */
  private loadContactDivSettingList(organizationCode: string): void {
    if (_isEmpty(organizationCode)) {
      this._contactDivSettingList = [];
      return;
    }
    // 実施手段取得失敗時は空リストでモーダル操作を継続
    this.contactService.searchContactDivSettingList(organizationCode).pipe(
      catchError(() => ObservableOf([] as IContactDivSetting[])),
      takeUntil(this.onDestroy$),
    ).subscribe(list => {
      this._contactDivSettingList = list || [];
    });
  }

  /** 一覧詳細検索と同じ DM 案内可否プルダウン文言（extract 区分コード用。All および未設定相当も「全て」） */
  private getDmSendDivDisplayName(code: string, suffix: string): string {
    switch (code) {
      case ContactExtractDmSendDiv.Send:
        return `DM案内可能な${suffix}`;
      case ContactExtractDmSendDiv.NoSend:
        return `DM案内不可の${suffix}`;
      case ContactExtractDmSendDiv.All:
      default:
        return '全て';
    }
  }

  /**
   * 設定取得後に組織名称を揃え、2列UIの選択キーとマスタ表示を同期します。
   */
  private initOrgMasterUiAfterSettingsLoaded(): void {
    if (this.organizationMultiSelectReadyReceived) {
      // 候補 ready 済みなら初期表示設定 GET 後も同じキャッシュを維持する
      const preserveCache = this.isOpenedFromContactListPage()
        && this.sharedContactListPageService.hasRegisteredSelectableOrganizations();
      this.syncOrgMasterUiAfterOrganizationContextReady({ preserveOrgMasterServiceCache: preserveCache });
    }
  }

  /** ready 由来の候補名称を organizationInfoList にマージします。 */
  private mergeOrganizationCandidateNames(): void {
    if (!_isEmpty(this.organizationCandidates)) {
      this.sharedContactListPageService.mergeOrganizationDisplayInfo(this.organizationCandidates);
    }
  }

  /**
   * 組織名称・候補組織を揃えたあと、一覧と同様に2列UIマスタを再取得します。
   */
  private syncOrgMasterUiAfterOrganizationContextReady(options?: { preserveOrgMasterServiceCache?: boolean }): void {
    if (!this.organizationMultiSelectReadyReceived) {
      return;
    }
    this.mergeOrganizationCandidateNames();
    const refreshOrgMasterUi = () => {
      const preserveCache = !_isNil(options) && options.preserveOrgMasterServiceCache === true;
      if (!preserveCache) {
        this.sharedContactListPageService.clearOrgMasterItemsCache();
      }
      this.loadOrgMasterKeysFromSavedCodeLists(() => {
        this.refreshOrgMasterSelectSearchMasters(() => {
          this.orgMasterRefreshToken++;
        });
      });
    };
    // 一覧起動時は親で組織情報済み。カレンダー起動時のみ全組織名称を API で補完する
    if (this.isOpenedFromContactListPage()) {
      refreshOrgMasterUi();
      return;
    }
    this.sharedContactListPageService.getAllOrganizationInformation().pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(() => {
      this.mergeOrganizationCandidateNames();
      refreshOrgMasterUi();
    });
  }

  /** コンタクト一覧からモーダルを開いたとき true */
  private isOpenedFromContactListPage(): boolean {
    return !_isNil(this.data) && !_isNil(this.data.contactListPageService);
  }

  /**
   * モーダル用2列UIマスタ再取得（一覧の scheduleRefresh は初回検索遅延・キャッシュ事前読み込み用。モーダルは forkJoin のみで足りる）。
   */
  private refreshOrgMasterSelectSearchMasters(done?: () => void): void {
    const complete = () => {
      if (done) {
        done();
      }
    };
    const runRefresh = () => {
      if (_isNil(this.orgMasterSelectSearchComponents)) {
        complete();
        return;
      }
      const components = this.orgMasterSelectSearchComponents.toArray();
      if (_isEmpty(components)) {
        complete();
        return;
      }
      ObservableForkJoin(_map(components, component => component.refreshMastersAndWait().pipe(
        catchError(() => ObservableOf([])),
      ))).pipe(
        takeUntil(this.onDestroy$),
      ).subscribe(() => complete());
    };
    if (!_isNil(this.orgMasterSelectSearchComponents) && this.orgMasterSelectSearchComponents.length > 0) {
      runRefresh();
      return;
    }
    if (_isNil(this.orgMasterSelectSearchComponents)) {
      complete();
      return;
    }
    this.orgMasterSelectSearchComponents.changes.pipe(
      take(1),
      takeUntil(this.onDestroy$),
    ).subscribe(() => runRefresh());
  }

  /**
   * 保存済みコード配列から2列UIの選択キーを復元します。
   * @param done 復元完了後の処理
   */
  private loadOrgMasterKeysFromSavedCodeLists(done?: () => void): void {
    this.restoreOrgMasterKeysFromDtlGrid();
    if (done) {
      done();
    }
  }

  /**
   * 決定前に2列UIの選択をコンタクト抽出対象失注理由／コンタクト内容コード配列へ解決します。
   * @returns 完了通知
   */
  private prepareInitDisplayGridForSave(): Observable<void> {
    // 決定前に2列UI選択をコンタクト抽出コード配列へ変換してから保存APIを呼ぶ
    return this.sharedContactListPageService.resolveInitDisplayGridForSave(
      this.contactInitDisplaySettingDtlGrid,
      this.contactLostReasonOrgMasterKeys,
      this.contactItemOrgMasterKeys,
      this.gridOrganizationCodes,
      this.loginUserOrganizationCode,
    ).pipe(
      map(grid => {
        this.contactInitDisplaySettingDtlGrid = grid;
      }),
    );
  }

  /** コンタクト抽出対象失注理由／コンタクト内容コード配列から2列UI選択キーを復元します。 */
  private restoreOrgMasterKeysFromDtlGrid(): void {
    // 失注理由コード配列
    const lostIn = this.sharedContactListPageService.normalizeContactExtractOrgMasterInCodes(
      this.contactInitDisplaySettingDtlGrid.contactExtractLostReasonCodeList,
    );
    this.contactLostReasonOrgMasterKeys = !_isEmpty(lostIn)
      ? this.sharedContactListPageService.orgMasterInCodesToUiKeys(lostIn, this.gridOrganizationCodes)
      : [];

    // コンタクト内容コード配列
    const itemIn = this.sharedContactListPageService.normalizeContactExtractOrgMasterInCodes(
      this.contactInitDisplaySettingDtlGrid.contacExtractItemCodeList,
    );
    this.contactItemOrgMasterKeys = !_isEmpty(itemIn)
      ? this.sharedContactListPageService.orgMasterInCodesToUiKeys(itemIn, this.gridOrganizationCodes)
      : [];
  }

  /** 選択外組織の2列UIキーを除去（一覧 contact-list-page と同じ判定） */
  private removeSelectKeysOfUnselectedOrganizations(): void {
    const orgCodes = this.sharedContactListPageService.getOrgMasterTargetOrgCodes(this.gridOrganizationCodes);
    if (_isEmpty(orgCodes)) {
      return;
    }
    const isTargetOrgKey = (key: string) => isOrgMasterSelectionKeyInOrganizations(key, orgCodes);
    this.contactLostReasonOrgMasterKeys = _filter(this.contactLostReasonOrgMasterKeys, isTargetOrgKey);
    this.contactItemOrgMasterKeys = _filter(this.contactItemOrgMasterKeys, isTargetOrgKey);
  }

  private validate() {
    if (!this.contactInitDisplaySettingDtlCalendar.organizationCode) {
      this.dialogService.warn('組織を入力してください。').subscribe(
        errorModalRef => {
          errorModalRef.hide();
        }
      );
    } else if (!this.validateStartInputValues() || !this.validateEndInputValues()) {
      this.dialogService.warn('対象期間（実施予定日）を入力してください。').subscribe(modalRef => modalRef.hide());
    } else {
      // 対象開始日
      const start = this.getApplyPeriodDate(
        this.contactInitDisplaySettingDtlGrid.applyStartPeriodCriteriaDiv,
        this.contactInitDisplaySettingDtlGrid.monthWeekDayDiv,
        this.contactInitDisplaySettingDtlGrid.applyStartNum
      );
      // 対象終了日
      const end = this.getApplyPeriodDate(
        this.contactInitDisplaySettingDtlGrid.applyEndPeriodCriteriaDiv,
        this.contactInitDisplaySettingDtlGrid.monthWeekDayDiv,
        this.contactInitDisplaySettingDtlGrid.applyEndNum
      );

      // 開始期間が終了期間よりも後の日付になっていた場合
      if (start > end) {
        this.dialogService.warn('対象期間が不正です。入力内容を確認してください。').subscribe(modalRef => modalRef.hide());
        // 期間が一年未満かチェック
      } else if (start.add(12, 'M') < end) {
        this.dialogService.warn('対象期間は1年以内で指定してください。').subscribe(modalRef => modalRef.hide());
      } else {
        return true;
      }
    }

    return false;
  }

  /**
   * 対象期間（開始）入力チェック
   * applyStartPeriodCriteriaDivがTodayの場合、常にtrueを返却。
   * applyStartPeriodCriteriaDivがToday以外の場合、applyStartNumが1以上であればtrueを返却。
   */
  validateStartInputValues(): boolean {
    if (this.contactInitDisplaySettingDtlGrid.applyStartPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today) {
      return true;
    } else {
      if (this.contactInitDisplaySettingDtlGrid.applyStartNum) {
        return true;
      }
    }

    return false;
  }

  /**
   * 対象期間（終了）入力チェック
   * applyEndPeriodCriteriaDivがTodayの場合、常にtrueを返却。
   * applyEndPeriodCriteriaDivがToday以外の場合、applyEndNumが1以上であればtrueを返却。
   */
  validateEndInputValues(): boolean {
    if (this.contactInitDisplaySettingDtlGrid.applyEndPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.Today) {
      return true;
    } else {
      if (this.contactInitDisplaySettingDtlGrid.applyEndNum) {
        return true;
      }
    }

    return false;
  }

  /**
   * 対象期間日の取得処理
   * @param applyPeriodCriteriaDiv 適用期間基準区分
   * @param monthWeekDayDiv 月週日区分
   * @param applyNum 適用値
   * @returns
   */
  private getApplyPeriodDate(applyPeriodCriteriaDiv: string, monthWeekDayDiv: string, applyNum: number) {
    let date = moment().clone();

    if (applyPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.PastFromToday) {
      switch (monthWeekDayDiv) {
        case MonthWeekDayDiv.Day:
          date = date.subtract(applyNum, 'days');
          break;
        case MonthWeekDayDiv.Week:
          date = date.subtract(applyNum, 'weeks');
          break;
        case MonthWeekDayDiv.Month:
          date = date.subtract(applyNum, 'M');
          break;
      }
    } else if (applyPeriodCriteriaDiv === ApplyPeriodCriteriaDiv.FutureFromToday) {
      switch (monthWeekDayDiv) {
        case MonthWeekDayDiv.Day:
          date = date.add(applyNum, 'days');
          break;
        case MonthWeekDayDiv.Week:
          date = date.add(applyNum, 'weeks');
          break;
        case MonthWeekDayDiv.Month:
          date = date.add(applyNum, 'M');
          break;
      }
    }

    return date;
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/contact-init-setting/contact-init-setting-modal.component.ts